import { useMemo, useState } from "react";
import { LifeBuoy } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, Segmented, StatCard, InfoRow, compactINR } from "@/components/tools/kit";
import { emergencyFund } from "@/lib/calculators";

const PARK = [
  { where: "Savings account", pct: "1 month", note: "Instant access for day-1 needs", color: "#2D6A4F" },
  { where: "Sweep-in FD / liquid fund", pct: "2–3 months", note: "1-day redemption, slightly better returns", color: "#2A6F7F" },
  { where: "Short-term / liquid mutual fund", pct: "Rest", note: "Beats inflation, T+1 access", color: "#D99B26" },
];

const MONTH_OPTIONS = [
  { value: 3, label: "3 months" },
  { value: 6, label: "6 months" },
  { value: 9, label: "9 months" },
  { value: 12, label: "1 year" },
];

export default function EmergencyFund() {
  const [months, setMonths] = useState(6);
  const [essential, setEssential] = useState(60000);
  const [nonEssential, setNonEssential] = useState(20000);
  const [includeNon, setIncludeNon] = useState("no");
  const [existing, setExisting] = useState(200000);
  const [stability, setStability] = useState("stable");

  const r = useMemo(
    () => emergencyFund({
      essential, nonEssential, includeNonEssential: includeNon === "yes",
      months, existingFund: existing, stability,
    }),
    [essential, nonEssential, includeNon, months, existing, stability]
  );

  const chosenInRange = months >= r.guide.min && months <= r.guide.max;

  return (
    <ToolLayout title="Emergency Fund" subtitle="Choose your cover, split essential vs non-essential, and see where to park it" icon={LifeBuoy} accent="#2A6F7F">
      <TwoCol
        left={
          <SectionCard title="Your situation">
            <div className="space-y-6">
              {/* Step 1 — months of cover */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-5 w-5 rounded-full bg-[#2A6F7F] text-white text-[11px] font-bold flex items-center justify-center">1</span>
                  <span className="text-xs uppercase font-semibold tracking-wider text-[#5C6A64]">Months of emergency fund to hold</span>
                </div>
                <Segmented testid="ef-months-choice" value={months} onChange={setMonths} options={MONTH_OPTIONS} />
              </div>

              {/* Step 2 — expense breakdown */}
              <div className="space-y-4 border-t border-dashed border-[#E8E3D9] pt-5">
                <div className="flex items-center gap-2">
                  <span className="h-5 w-5 rounded-full bg-[#2A6F7F] text-white text-[11px] font-bold flex items-center justify-center">2</span>
                  <span className="text-xs uppercase font-semibold tracking-wider text-[#5C6A64]">Break down your monthly expenses</span>
                </div>
                <Field label="Essential (mandatory)">
                  <MoneyInput testid="ef-essential" value={essential} onChange={setEssential}
                    helper="Rent, EMIs, education, health, loan repayment" />
                </Field>
                <Field label="Non-essential (lifestyle)">
                  <MoneyInput testid="ef-nonessential" value={nonEssential} onChange={setNonEssential}
                    helper="Dining out, entertainment, travel" />
                </Field>
                <Field label="Include non-essential in the fund?">
                  <Segmented testid="ef-include-non" value={includeNon} onChange={setIncludeNon}
                    options={[{ value: "no", label: "No · essentials only" }, { value: "yes", label: "Yes · full lifestyle" }]} />
                </Field>
              </div>

              {/* existing */}
              <div className="border-t border-dashed border-[#E8E3D9] pt-5">
                <Field label="Emergency fund already saved">
                  <MoneyInput testid="ef-existing" value={existing} onChange={setExisting} helper={compactINR(existing)} />
                </Field>
              </div>

              {/* Income stability — recommendation only */}
              <div className="border-t border-dashed border-[#E8E3D9] pt-5">
                <Field label="Income stability (guides our recommendation)">
                  <Segmented testid="ef-stability" value={stability} onChange={setStability}
                    options={[{ value: "stable", label: "Stable" }, { value: "variable", label: "Variable" }, { value: "single", label: "Single income" }]} />
                </Field>
                <p className="text-xs text-[#8A9790] mt-2 leading-relaxed" data-testid="ef-stability-note">
                  Stable income: keep <strong>3–6 months</strong>. Variable income: keep <strong>6–9 months</strong>.
                  Single income / risky job: keep <strong>9–12 months</strong>.
                </p>
              </div>
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard testid="ef-months" label="Cover chosen" value={months === 12 ? "1 year" : `${months} mo`} accent="#2A6F7F" />
              <StatCard testid="ef-target" label="Target fund" value={compactINR(r.target)} accent="#2D6A4F" big />
              <StatCard testid="ef-gap" label={r.covered ? "You're covered" : "Still to save"} value={r.covered ? "✓ Done" : compactINR(r.gap)} accent={r.covered ? "#2D6A4F" : "#D96B43"} />
            </div>

            <div className={`rounded-2xl border p-4 ${chosenInRange ? "bg-[#2D6A4F]/8 border-[#2D6A4F]/25" : "bg-[#D99B26]/10 border-[#D99B26]/30"}`} data-testid="ef-recommendation">
              <p className="text-sm text-[#3A463F]">
                Based on your <strong>{r.guide.label.toLowerCase()}</strong>, we recommend an emergency fund of <strong>{r.guide.range}</strong>.
                {chosenInRange
                  ? ` Your chosen ${months === 12 ? "1 year" : months + " months"} fits this nicely.`
                  : ` You've chosen ${months === 12 ? "1 year" : months + " months"} — consider aligning with the recommended range.`}
              </p>
            </div>

            <SectionCard title="The maths">
              <InfoRow label="Essential expenses" value={compactINR(essential)} />
              <InfoRow label="Non-essential expenses" value={`${includeNon === "yes" ? "" : "excluded · "}${compactINR(nonEssential)}`} />
              <InfoRow label="Monthly need used" value={compactINR(r.monthlyNeed)} strong />
              <InfoRow label={`× ${months === 12 ? "12" : months} months`} value={compactINR(r.target)} strong />
              <InfoRow label="Already saved" value={compactINR(existing)} />
              <InfoRow label={r.covered ? "Surplus" : "Gap to fund"} value={r.covered ? compactINR(existing - r.target) : compactINR(r.gap)} strong />
            </SectionCard>

            <SectionCard title="Recommended options to park" testid="ef-parking">
              <div className="space-y-3">
                {PARK.map((p) => (
                  <div key={p.where} className="flex items-start gap-3 rounded-xl bg-[#FBF9F5] border border-[#EFE9DD] p-3">
                    <span className="mt-1 h-3 w-3 rounded-full flex-shrink-0" style={{ background: p.color }} />
                    <div>
                      <p className="font-heading font-bold text-sm text-[#1A231F]">{p.where} <span className="text-[#8A9790] font-normal">· {p.pct}</span></p>
                      <p className="text-xs text-[#5C6A64]">{p.note}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#8A9790] mt-3">Keep it separate from your investing money — this is insurance, not returns.</p>
            </SectionCard>
          </>
        }
      />
    </ToolLayout>
  );
}
